import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, AlertCircle, Clock, CheckCircle, Archive, Send, ChevronDown, X, User } from 'lucide-react';
import { useAppContext, Ticket } from '../../context/AppContext';
export function TicketManager() {
  const {
    tickets,
    updateTicket,
    staff
  } = useAppContext();
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState<'active' | 'archive'>('active');
  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const activeTickets = tickets.filter(t => t.status !== 'closed');
  const archivedTickets = tickets.filter(t => t.status === 'closed');
  const displayTickets = view === 'active' ? activeTickets : archivedTickets;
  const filteredTickets = displayTickets.filter(t => {
    if (view === 'archive') return true;
    if (filter === 'all') return true;
    return t.status === filter;
  });
  const statusCounts = {
    all: activeTickets.length,
    open: activeTickets.filter(t => t.status === 'open').length,
    'in-progress': activeTickets.filter(t => t.status === 'in-progress').length,
    resolved: activeTickets.filter(t => t.status === 'resolved').length
  };
  const handleStatusChange = (ticketId: string, newStatus: Ticket['status']) => {
    updateTicket(ticketId, {
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
    if (newStatus === 'closed') {
      setExpandedTicket(null);
    }
  };
  const handleReply = (ticketId: string) => {
    if (!replyText.trim()) return;
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;
    const newMessage = {
      id: `m${Date.now()}`,
      sender: 'Admin Staff',
      role: 'staff' as const,
      content: replyText.trim(),
      createdAt: new Date().toISOString()
    };
    updateTicket(ticketId, {
      messages: [...ticket.messages, newMessage],
      updatedAt: new Date().toISOString(),
      status: ticket.status === 'open' ? 'in-progress' : ticket.status
    });
    setReplyText('');
  };
  const handleAssign = (ticketId: string, staffId: string) => {
    updateTicket(ticketId, {
      assignedTo: staffId,
      updatedAt: new Date().toISOString()
    });
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'high':
        return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case 'medium':
        return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      default:
        return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'in-progress':
        return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
      case 'resolved':
        return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      case 'closed':
        return 'text-slate-400 bg-slate-700/30 border-slate-600/30';
      default:
        return 'bg-slate-800 text-slate-300 border-slate-700';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle size={14} data-id="element-1030" />;
      case 'in-progress':
        return <Clock size={14} data-id="element-1031" />;
      case 'resolved':
        return <CheckCircle size={14} data-id="element-1032" />;
      case 'closed':
        return <Archive size={14} data-id="element-1033" />;
      default:
        return <MessageSquare size={14} data-id="element-1034" />;
    }
  };
  const getStaffName = (staffId?: string) => {
    if (!staffId) return 'Unassigned';
    const member = staff.find(s => s.id === staffId);
    return member ? member.name : 'Unknown';
  };
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="space-y-8" data-id="element-1035">
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} data-id="element-1036">
        <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1037">
          Support Tickets
        </h1>
        <p className="text-slate-400" data-id="element-1038">
          Manage customer inquiries and technical support.
        </p>
      </motion.div>

      {/* View Toggle: Active vs Archive */}
      <div className="flex items-center gap-4" data-id="element-1039">
        <div className="flex bg-slate-950 border border-slate-800 rounded-xl p-1" data-id="element-1040">
          <button onClick={() => {
          setView('active');
          setFilter('all');
        }} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${view === 'active' ? 'bg-fox-600 text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1041">
            <MessageSquare size={16} data-id="element-1042" />
            Active Tickets
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === 'active' ? 'bg-white/20' : 'bg-slate-800'}`} data-id="element-1043">
              {activeTickets.length}
            </span>
          </button>
          <button onClick={() => setView('archive')} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${view === 'archive' ? 'bg-fox-600 text-white' : 'text-slate-400 hover:text-white'}`} data-id="element-1044">
            <Archive size={16} data-id="element-1045" />
            Closed Archive
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${view === 'archive' ? 'bg-white/20' : 'bg-slate-800'}`} data-id="element-1046">
              {archivedTickets.length}
            </span>
          </button>
        </div>
      </div>

      {/* Status Filters (active view only) */}
      {view === 'active' && <div className="flex gap-2 flex-wrap" data-id="element-1047">
          {(['all', 'open', 'in-progress', 'resolved'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${filter === f ? 'bg-fox-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'}`} data-id="element-1048">
              {f === 'all' ? 'All' : f === 'in-progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${filter === f ? 'bg-white/20' : 'bg-slate-800'}`} data-id="element-1049">
                {statusCounts[f]}
              </span>
            </button>)}
        </div>}

      {/* Archive Info Banner */}
      {view === 'archive' && <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex items-center gap-3" data-id="element-1050">
          <Archive size={20} className="text-slate-500 shrink-0" data-id="element-1051" />
          <p className="text-slate-400 text-sm" data-id="element-1052">
            Closed tickets are stored here for review and record-keeping. These
            tickets have been resolved and closed by staff.
          </p>
        </div>}

      {/* Ticket List */}
      <div className="grid grid-cols-1 gap-4" data-id="element-1053">
        {filteredTickets.map((ticket, i) => <motion.div key={ticket.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className={`bg-slate-950 border rounded-xl overflow-hidden transition-colors ${expandedTicket === ticket.id ? 'border-fox-500/50' : 'border-slate-800 hover:border-slate-700'}`} data-id="element-1054">
            {/* Ticket Header */}
            <div className="p-5 cursor-pointer" onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)} data-id="element-1055">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1056">
                <div className="flex-1" data-id="element-1057">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1058">
                    <span className="text-slate-500 font-mono text-sm" data-id="element-1059">
                      {ticket.id}
                    </span>
                    <h3 className="text-lg font-bold text-white" data-id="element-1060">
                      {ticket.subject}
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400" data-id="element-1061">
                    <span data-id="element-1062">
                      From:{' '}
                      <span className="text-slate-300" data-id="element-1063">
                        {ticket.clientName}
                      </span>
                    </span>
                    <span data-id="element-1064">•</span>
                    <span data-id="element-1065">
                      Assigned:{' '}
                      <span className="text-slate-300" data-id="element-1066">
                        {getStaffName(ticket.assignedTo)}
                      </span>
                    </span>
                    <span data-id="element-1067">•</span>
                    <span data-id="element-1068">
                      {ticket.messages.length} message
                      {ticket.messages.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3" data-id="element-1069">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`} data-id="element-1070">
                    {ticket.priority.toUpperCase()}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`} data-id="element-1071">
                    {getStatusIcon(ticket.status)}
                    {ticket.status === 'in-progress' ? 'IN PROGRESS' : ticket.status.toUpperCase()}
                  </span>
                  <ChevronDown size={18} className={`text-slate-500 transition-transform ${expandedTicket === ticket.id ? 'rotate-180' : ''}`} data-id="element-1072" />
                </div>
              </div>
            </div>

            {/* Expanded Ticket Detail */}
            <AnimatePresence data-id="element-1073">
              {expandedTicket === ticket.id && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-1074">
                  <div className="border-t border-slate-800" data-id="element-1075">
                    {/* Status & Assignment Controls */}
                    <div className="p-4 bg-slate-900/50 border-b border-slate-800 flex flex-wrap items-center gap-4" data-id="element-1076">
                      <div className="flex items-center gap-2" data-id="element-1077">
                        <span className="text-xs text-slate-500 font-medium" data-id="element-1078">
                          Status:
                        </span>
                        {(['open', 'in-progress', 'resolved', 'closed'] as const).map(status => <button key={status} onClick={e => {
                    e.stopPropagation();
                    handleStatusChange(ticket.id, status);
                  }} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors border ${ticket.status === status ? getStatusColor(status) : 'border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-600'}`} data-id="element-1079">
                            {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                          </button>)}
                      </div>

                      <div className="flex items-center gap-2 ml-auto" data-id="element-1080">
                        <span className="text-xs text-slate-500 font-medium" data-id="element-1081">
                          Assign:
                        </span>
                        <select value={ticket.assignedTo || ''} onChange={e => handleAssign(ticket.id, e.target.value)} onClick={e => e.stopPropagation()} className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-1 text-xs text-white focus:border-fox-500 focus:outline-none" data-id="element-1082">
                          <option value="" data-id="element-1083">Unassigned</option>
                          {staff.map(s => <option key={s.id} value={s.id} data-id="element-1084">
                              {s.name}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    {/* Message Thread */}
                    <div className="p-5 space-y-4 max-h-80 overflow-y-auto" data-id="element-1085">
                      {ticket.messages.map(msg => <div key={msg.id} className={`flex gap-3 ${msg.role === 'staff' ? 'flex-row-reverse' : ''}`} data-id="element-1086">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${msg.role === 'staff' ? 'bg-fox-500/20 text-fox-500' : 'bg-blue-500/20 text-blue-500'}`} data-id="element-1087">
                            {msg.role === 'staff' ? 'S' : 'C'}
                          </div>
                          <div className={`max-w-[75%] ${msg.role === 'staff' ? 'text-right' : ''}`} data-id="element-1088">
                            <div className={`rounded-xl px-4 py-3 ${msg.role === 'staff' ? 'bg-fox-600/10 border border-fox-500/20' : 'bg-slate-900 border border-slate-800'}`} data-id="element-1089">
                              <p className="text-sm text-slate-200" data-id="element-1090">
                                {msg.content}
                              </p>
                            </div>
                            <div className={`flex items-center gap-2 mt-1 ${msg.role === 'staff' ? 'justify-end' : ''}`} data-id="element-1091">
                              <span className="text-xs text-slate-600" data-id="element-1092">
                                {msg.sender}
                              </span>
                              <span className="text-xs text-slate-700" data-id="element-1093">•</span>
                              <span className="text-xs text-slate-600" data-id="element-1094">
                                {formatDate(msg.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>)}
                    </div>

                    {/* Reply Box (only for non-closed tickets) */}
                    {ticket.status !== 'closed' && <div className="p-4 border-t border-slate-800 bg-slate-900/30" data-id="element-1095">
                        <div className="flex gap-3" data-id="element-1096">
                          <input type="text" value={expandedTicket === ticket.id ? replyText : ''} onChange={e => setReplyText(e.target.value)} onClick={e => e.stopPropagation()} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleReply(ticket.id);
                    }
                  }} placeholder="Type your reply..." className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white text-sm focus:border-fox-500 focus:outline-none" data-id="element-1097" />
                          <button onClick={e => {
                    e.stopPropagation();
                    handleReply(ticket.id);
                  }} disabled={!replyText.trim()} className="bg-fox-600 hover:bg-fox-500 disabled:opacity-40 text-white px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm" data-id="element-1098">
                            <Send size={14} data-id="element-1099" /> Reply
                          </button>
                        </div>
                        {ticket.status === 'resolved' && <p className="text-xs text-slate-500 mt-2" data-id="element-1100">
                            This ticket is resolved. You can close it to move it
                            to the archive.
                          </p>}
                      </div>}

                    {/* Closed ticket notice */}
                    {ticket.status === 'closed' && <div className="p-4 border-t border-slate-800 bg-slate-900/30 flex items-center gap-3" data-id="element-1101">
                        <Archive size={16} className="text-slate-500" data-id="element-1102" />
                        <p className="text-sm text-slate-500" data-id="element-1103">
                          This ticket is closed and archived. You can reopen it
                          by changing the status above.
                        </p>
                      </div>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>)}

        {filteredTickets.length === 0 && <div className="text-center py-12 text-slate-500 bg-slate-950 rounded-xl border border-slate-800" data-id="element-1104">
            <div className="mb-3" data-id="element-1105">
              {view === 'archive' ? <Archive size={40} className="mx-auto text-slate-700" data-id="element-1106" /> : <MessageSquare size={40} className="mx-auto text-slate-700" data-id="element-1107" />}
            </div>
            {view === 'archive' ? 'No closed tickets in the archive yet.' : 'No tickets found for this filter.'}
          </div>}
      </div>
    </div>;
}